import React, { useState, useEffect } from 'react';
import { useAuth } from '../components/AuthGuard';
import { db } from '../firebase/config';
import { doc, getDoc, collection, query, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

const MyPortfolioPage = () => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('onboarding'); // 'profile', 'onboarding', 'portfolio'
    
    // Status
    const [paymentStatus, setPaymentStatus] = useState('missing');
    const [tillNumber, setTillNumber] = useState('');
    const [mpesaName, setMpesaName] = useState('');
    const [professionalRole, setProfessionalRole] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [saveError, setSaveError] = useState('');
    
    // Tracks
    const [myTracks, setMyTracks] = useState([]);

    useEffect(() => {
        if (!user) return;
        
        const fetchArtistData = async () => {
            try {
                const artistDoc = await getDoc(doc(db, 'artists', user.uid));
                if (artistDoc.exists()) {
                    const data = artistDoc.data();
                    if (data.payment_info) {
                        setTillNumber(data.payment_info.tillNumber || '');
                        setMpesaName(data.payment_info.mpesaName || '');
                        setPaymentStatus(data.payment_info.status || 'missing');
                    }
                    // Load registered professional role
                    if (data.professionalRole) setProfessionalRole(data.professionalRole);
                }
            } catch(e) { console.error('Error fetching artist data', e); }
            
            // 2. Fetch Portfolio (Mocked filter by current user)
            try {
                const q = query(collection(db, 'projects')); // Ideally filter by artistId == user.uid
                const snap = await getDocs(q);
                const t = [];
                snap.forEach(d => t.push({ id: d.id, ...d.data() }));
                setMyTracks(t);
            } catch(e) { console.error("Error fetching tracks", e); }
        };

        fetchArtistData();
    }, [user]);

    const handleOnboardSubmit = async (e) => {
        e.preventDefault();
        if (!professionalRole) {
            setSaveError('Please select your professional role. This is required for VPRE ledger attribution.');
            return;
        }
        setSaveError('');
        setIsSaving(true);
        try {
            // Call the Cloud Function — this triggers the KES 1 verification ping
            // and saves professionalRole alongside payment_info atomically.
            const onboardArtist = httpsCallable(getFunctions(), 'onboardArtist');
            await onboardArtist({ tillNumber, mpesaName, professionalRole });
            setPaymentStatus('verified');
        } catch (error) {
            console.error(error);
            setSaveError(error.message || 'Onboarding failed. Please try again.');
        }
        setIsSaving(false);
    };

    return (
        <div className="min-h-screen bg-black text-white p-8 ml-64 font-['Montserrat']">
            <h1 className="text-3xl font-black uppercase tracking-tighter mb-8">My Portfolio</h1>

            {/* TABS */}
            <div className="flex space-x-8 border-b border-white/10 mb-8">
                {['profile', 'onboarding', 'portfolio'].map(tab => (
                    <button
                        key={tab}
                        onClick={() => setActiveTab(tab)}
                        className={`pb-4 uppercase tracking-widest text-xs font-bold transition-colors ${activeTab === tab ? 'text-white border-b-2 border-white' : 'text-white/40 hover:text-white/80'}`}
                    >
                        {tab.replace('-', ' ')}
                    </button>
                ))}
            </div>

            {/* TAB CONTENT */}
            <div className="max-w-4xl">
                
                {activeTab === 'profile' && (
                    <div className="space-y-6">
                        <h2 className="text-xl font-bold">Edit Profile</h2>
                        <p className="text-sm text-zinc-500">Update your public aesthetic and bio here.</p>
                        {/* Simplistic mock profile form */}
                        <input className="w-full bg-zinc-900 border border-white/10 rounded-xl p-4 text-white" placeholder="Display Name" />
                        <textarea className="w-full bg-zinc-900 border border-white/10 rounded-xl p-4 text-white min-h-[150px]" placeholder="Bio (Max 300 chars)" />
                        <button className="px-6 py-3 bg-white text-black font-bold uppercase rounded-xl">Save Profile</button>
                    </div>
                )}

                {activeTab === 'onboarding' && (
                    <div className="space-y-6 bg-zinc-900 p-8 rounded-2xl border border-white/5">
                        <div className="flex items-center justify-between">
                            <div>
                                <h2 className="text-xl font-bold uppercase tracking-widest mb-1">Payment Engine Pipeline</h2>
                                <p className="text-sm text-zinc-500">Register your M-Pesa Till to receive automated routing payloads.</p>
                            </div>
                            <div className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-widest ${paymentStatus === 'verified' ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'}`}>
                                Status: {paymentStatus}
                            </div>
                        </div>

                        <form onSubmit={handleOnboardSubmit} className="space-y-4 mt-8">
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Till Number</label>
                                <input 
                                    required
                                    type="text" 
                                    value={tillNumber}
                                    onChange={e => setTillNumber(e.target.value)}
                                    className="w-full bg-black border border-white/10 rounded-xl p-4 text-white" 
                                    placeholder="Enter 6-digit Till" 
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Registered Owner Name</label>
                                <input 
                                    required
                                    type="text" 
                                    value={mpesaName}
                                    onChange={e => setMpesaName(e.target.value)}
                                    className="w-full bg-black border border-white/10 rounded-xl p-4 text-white" 
                                    placeholder="Exact Name on Till" 
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">
                                    Professional Role <span className="text-red-400">*</span>
                                </label>
                                <p className="text-[10px] text-zinc-500 mb-3">This is written permanently to your VPRE blocks. Choose your primary role accurately.</p>
                                <select
                                    required
                                    value={professionalRole}
                                    onChange={e => setProfessionalRole(e.target.value)}
                                    className="w-full bg-black border border-white/10 rounded-xl p-4 text-white appearance-none cursor-pointer"
                                >
                                    <option value="">— Select Your Role —</option>
                                    <optgroup label="Primary & Featured">
                                        <option value="Lead Artist">Lead Artist</option>
                                        <option value="Featured Artist">Featured Artist</option>
                                        <option value="Vocalist">Vocalist</option>
                                        <option value="Background Vocals">Background Vocals</option>
                                        <option value="Recording Artist">Recording Artist</option>
                                    </optgroup>
                                    <optgroup label="Production">
                                        <option value="Producer">Producer</option>
                                        <option value="Beat Producer">Beat Producer</option>
                                        <option value="Co-Producer">Co-Producer</option>
                                        <option value="Executive Producer">Executive Producer</option>
                                        <option value="Drum Programming">Drum Programming</option>
                                    </optgroup>
                                    <optgroup label="Engineering">
                                        <option value="Mix Engineer">Mix Engineer</option>
                                        <option value="Mastering Engineer">Mastering Engineer</option>
                                        <option value="Recording Engineer">Recording Engineer</option>
                                        <option value="Dolby Atmos Engineer">Dolby Atmos Engineer</option>
                                    </optgroup>
                                    <optgroup label="Creative">
                                        <option value="Songwriter">Songwriter</option>
                                        <option value="Composer">Composer</option>
                                        <option value="Lyricist">Lyricist</option>
                                        <option value="Arranger">Arranger</option>
                                    </optgroup>
                                    <optgroup label="Business">
                                        <option value="Label Manager">Label Manager</option>
                                        <option value="A&R">A&R</option>
                                        <option value="Publisher">Publisher</option>
                                    </optgroup>
                                </select>
                            </div>
                            {saveError && (
                                <p className="text-red-400 text-xs font-bold">{saveError}</p>
                            )}
                            <button 
                                disabled={isSaving}
                                type="submit" 
                                className="w-full py-4 bg-white text-black font-bold uppercase tracking-widest rounded-xl hover:bg-zinc-200 disabled:opacity-50"
                            >
                                {isSaving ? 'Verifying Pipeline...' : 'Deploy Payment Pipeline'}
                            </button>
                        </form>
                        
                        <div className="mt-8 p-6 bg-black rounded-xl border border-white/5">
                            <h3 className="text-sm font-bold text-zinc-400 mb-2">How it works:</h3>
                            <ul className="text-xs text-zinc-500 space-y-2 list-disc list-inside">
                                <li>When fans subscribe or buy your tracks, 80% routing flows directly into this Till.</li>
                                <li>The engine uses Safaricom B2C automation to settle ledgers instantly.</li>
                                <li>You must register a Business Till Number, not a personal phone number.</li>
                            </ul>
                        </div>
                    </div>
                )}

                {activeTab === 'portfolio' && (
                    <div className="space-y-6">
                        <h2 className="text-xl font-bold uppercase tracking-widest mb-4">Live Portfolio Performance</h2>
                        <div className="grid gap-4">
                            {myTracks.map(track => (
                                <div key={track.id} className="p-4 bg-zinc-900 border border-white/5 rounded-xl flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 bg-black rounded-lg overflow-hidden border border-white/10 flex-shrink-0">
                                            {track.artwork ? <img src={track.artwork} className="w-full h-full object-cover" alt="" /> : null}
                                        </div>
                                        <div>
                                            <h4 className="font-bold">{track.title}</h4>
                                            <p className="text-xs text-zinc-500">MIVI Score: {track.totalMiviScore || 0}</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <div className="text-xs font-bold uppercase tracking-widest text-[#FACC15]">
                                            {track.revenue_split ? `Split: A${track.revenue_split.artist}% / L${track.revenue_split.label}%` : 'Split: Default A80/L20'}
                                        </div>
                                        <div className="text-[10px] text-zinc-500 mt-1">PONT Ledger: ACTIVE</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

            </div>
        </div>
    );
};

export default MyPortfolioPage;
